<template>
  <el-select
    ref="selector"
    v-model="internalValue"
    :clearable="clearable"
    :collapse-tags="collapseTags"
    :filter-method="handleFilterSearchChange"
    :placeholder="placeholder"
    :value-key="itemValueKey"
    filterable
    multiple
    :size="size"
    v-bind="$attrs"
    :no-data-text="emptyText"
    @change="handleChange"
    @visible-change="handleVisibleChange"
  >
    <el-option
      v-for="item in filteredItems"
      :key="item.key"
      :class="item.class || ''"
      :disabled="item.disabled"
      :label="item.label"
      :value="item.value"
    >
      <span class="label-left" v-html="highlight(item.label)" />
      <span v-show="item.description" class="label-right mr-3" v-html="highlight(item.description)">
      </span>
    </el-option>
  </el-select>
</template>

<script>
import filterMixin from "@/mixins/filter";
import _ from "lodash";
import { sortByInList } from "@/utils/sorters";
import { v4 as idGenerator } from "uuid";
import { escapeRegExp } from "@/utils/regexp";

export default {
  name: "MultipleSelect",

  mixins: [filterMixin],

  inheritAttrs: false,

  props: {
    value: {
      type: [Array, Number, String],
      default: () => [],
    },
    size: {
      type: String,
      default: "mini",
    },
    generateKey: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    items: {
      type: Array,
      required: true,
    },
    itemLabel: {
      type: String,
      default: "label",
    },
    itemDescription: {
      type: String,
      default: null,
    },
    itemValue: {
      type: String,
      default: "id",
    },
    itemValueKey: {
      type: String,
      default: "value",
    },
    itemKey: {
      type: String,
    },
    allItems: {
      type: Boolean,
      default: true,
    },
    allItemsSelected: {
      type: Boolean,
      default: null,
    },
    collapseTags: {
      type: Boolean,
      default: true,
    },
    allItemsLabel: {
      type: String,
      default: "Todos los elementos",
    },
    allItemsValue: {
      type: [Array, Number, String],
      required: false,
    },
    allItemsClass: {
      type: String,
    },
    allItemsDisabled: {
      type: Boolean,
      default: false,
    },
    selectedOnTop: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "Seleccionar",
    },
    tabindex: {
      type: String,
    },
    requireFilter: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: "Sin opciones",
    },
  },

  data() {
    return {
      internalValue: null,
      textFilter: "",
    };
  },

  computed: {
    formattedItems() {
      let itemsFormatted = this.items.map((item) => {
        let key = this.itemKey ? item[this.itemKey] : item[this.itemValue];
        if (this.generateKey) {
          key = idGenerator();
        }
        return {
          key,
          value: item[this.itemValue],
          label: item[this.itemLabel],
          class: item.class,
          disabled: !!item.disabled,
          description: this.itemDescription ? item[this.itemDescription] : "",
        };
      });
      if (this.selectedOnTop) {
        if (this.value && this.value.length > 0) {
          itemsFormatted = itemsFormatted.sort(sortByInList(this.value, "value"));
        }
      }
      if (this.allItems) {
        itemsFormatted.unshift({
          value: 0,
          label: this.allItemsLabel,
          class: this.allItemsClass,
          disabled: this.allItemsDisabled,
        });
      }
      return itemsFormatted;
    },

    filteredItems() {
      const items = this.formattedItems;
      if (!this.textFilter) {
        if (!this.textFilter && this.requireFilter && this.value?.length > 0) {
          return items.filter((item) => this.value?.includes(item.value));
        }
        if (this.requireFilter) {
          return [];
        }
        return items;
      }
      const text = this.textFilter;
      const regexp = new RegExp(escapeRegExp(text), "i");
      return items.filter((item) => {
        let text = item.label;
        if (item.description) {
          text = `${text} ${item.description}`;
        }
        return text.match(regexp);
      });
    },

    allItemsComputedValue() {
      return this.allItemsValue !== undefined
        ? this.allItemsValue
        : this.items.map((i) => i[this.itemValue]);
    },

    areAllItemsSelected() {
      if (this.allItemsSelected !== null) {
        return this.allItemsSelected;
      }
      return (
        this.items.length > 0 &&
        JSON.parse(JSON.stringify(this.value)).length >= this.allItemsComputedValue.length &&
        _.isEqual(this.value, this.allItemsComputedValue)
      );
    },
  },

  watch: {
    value(v) {
      this.parseExternalValue(v);
    },
    items() {
      this.internalValue = [];
    },
  },

  created() {
    this.parseExternalValue(this.value);
  },

  mounted() {
    this.interval = setInterval(() => {
      if (this.$refs.selector && this.tabindex) {
        const input = this.$refs.selector.$el.querySelector("input");
        if (input) {
          input.tabIndex = this.tabindex;
        }
      }
    }, 100);
  },

  beforeDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  },

  methods: {
    close() {
      this.$refs.selector.handleClose();
    },

    focus() {
      this.$refs.selector.focus();
    },

    handleFilterItemsChange(text) {
      this.textFilter = text;
    },

    handleFilterSearchChange(text) {
      this.textFilter = text;
    },

    handleChange() {
      const lastItem = this.internalValue.slice(-1)[0];
      lastItem === 0
        ? (this.internalValue = [0])
        : (this.internalValue = this.internalValue.filter((sub) => sub !== 0));
      this.fireInput();
    },

    fireInput() {
      let finalValue = this.internalValue;
      if (finalValue.length === 1 && finalValue[0] === 0) {
        finalValue = this.allItemsComputedValue;
      }
      this.$emit("input", finalValue);
      this.$emit("change", finalValue);
    },

    async handleVisibleChange(visible) {
      if (visible && this.items.length < 1) {
        this.$emit("fetch");
      }
      if (!visible && !this.value && this.isRequired && this.textFilter !== "") {
        this.$message({
          type: "error",
          message: "No se ha seleccionado ningún elemento",
        });
      }
      this.textFilter = "";
    },

    parseExternalValue(v) {
      if (v && this.areAllItemsSelected && this.allItems) {
        this.internalValue = [0];
      } else {
        this.internalValue = v;
      }
    },
  },
};
</script>

<style>
.label-left {
  float: left;
  line-height: 16px;
  padding-right: 20px;
  margin: 9px 0;
  /* width: 75%; */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.label-right {
  float: right;
  overflow: hidden;
  color: #8492a6;
  font-size: 13px;
  line-height: 16px;
  margin: 9px 0;
}

.not-set .el-input__inner {
  background: #ff000014;
}
</style>
